import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Edit2, Trash2 } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";

const empty = {
  title: "", slug: "", featured_image: "", excerpt: "", content: "",
  author: "ZaykaQR Team", seo_title: "", seo_description: "", seo_keywords: "",
  status: "published",
};

export const AdminBlogs = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/blogs").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (b) => { setEditing(b); setForm({ ...empty, ...b }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/admin/blogs/${editing.id}`, form);
      else await api.post("/admin/blogs", form);
      toast.success("Saved");
      setOpen(false); load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const del = async (b) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blogs/${b.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-blogs">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Blog posts</h1>
          <p className="text-sm text-zq-muted mt-1">Manage SEO content and stories.</p>
        </div>
        <button className="zq-btn zq-btn-primary" onClick={startNew} data-testid="create-blog-btn"><Plus size={18}/> New post</button>
      </div>

      <div className="zq-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zq-orange-light text-left">
            <tr>
              <th className="p-3">Title</th>
              <th className="p-3">Slug</th>
              <th className="p-3">Status</th>
              <th className="p-3">Date</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-zq-muted">No posts yet.</td></tr>
            )}
            {list.map((b) => (
              <tr key={b.id} className="border-t border-zq-border">
                <td className="p-3 font-medium">{b.title}</td>
                <td className="p-3 text-xs font-mono">/blog/{b.slug}</td>
                <td className="p-3">
                  <span className={`zq-pill ${b.status === "published" ? "zq-pill-green" : "zq-pill-gray"}`}>{b.status}</span>
                </td>
                <td className="p-3 text-xs">{new Date(b.created_at).toLocaleDateString("en-IN")}</td>
                <td className="p-3 text-right space-x-1">
                  <button className="zq-btn zq-btn-ghost !px-2" onClick={() => startEdit(b)} data-testid={`edit-blog-${b.slug}`}><Edit2 size={16}/></button>
                  <button className="zq-btn zq-btn-ghost !px-2 text-red-600" onClick={() => del(b)} data-testid={`delete-blog-${b.slug}`}><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="zq-card w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <h3 className="text-xl font-bold">{editing ? "Edit post" : "New post"}</h3>
              <button onClick={() => setOpen(false)}><X size={18}/></button>
            </div>
            <form className="mt-5 space-y-3" onSubmit={save}>
              <input className="zq-input" placeholder="Title *" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-title"/>
              <input className="zq-input" placeholder="Slug (auto if empty)" value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="blog-slug"/>
              <ImageUpload
                value={form.featured_image}
                onChange={(url) => setForm({ ...form, featured_image: url })}
                folder="zaykaqr/blogs/featured"
                label="Featured Image"
                aspect="aspect-video"
                testId="blog-featured-upload"
              />
              <input className="zq-input" placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}/>
              <textarea className="zq-input" rows={8} placeholder="Content *" required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="blog-content"/>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className="zq-input" placeholder="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })}/>
                <select className="zq-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              <input className="zq-input" placeholder="SEO Title" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })}/>
              <input className="zq-input" placeholder="SEO Description" value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })}/>
              <input className="zq-input" placeholder="Keywords (comma separated)" value={form.seo_keywords} onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })}/>
              <div className="flex gap-2 mt-2">
                <button type="button" className="zq-btn zq-btn-outline flex-1" onClick={() => setOpen(false)}>Cancel</button>
                <button className="zq-btn zq-btn-primary flex-1" data-testid="blog-submit">{editing ? "Save" : "Publish"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
